import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const KNOWN_MODAL_CLASSES = [
    'showCreationLocationModal',
    'showImportCollectionModal',
    'showBimboShareModal',
    'modal-open',
    'offcanvas-open',
    'on-drag',
    'onDrag',
]

/**
 * True while React still owns this node — meaning React will remove it itself, and we must not.
 */
const isReactOwned = (element: Element): boolean => {
    const reactKey = Object.keys(element).find(
        (key) => key.startsWith('__reactFiber$') || key.startsWith('__reactProps$'),
    )

    if (!reactKey) {
        return false
    }

    return undefined !== (element as unknown as Record<string, unknown>)[reactKey]
}

/**
 * Safely cleans up residual body classes, inline scroll locks, and stranded backdrop elements on route navigation.
 */
export const useRouteCleanup = () => {
    const location = useLocation()

    useEffect(() => {
        // Body classes and scroll locks: always safe to clear. Nothing else owns them, and each
        // modal's own useBodyClass() already removes its class on unmount — this is belt-and-braces
        // for the abrupt-unmount case.
        KNOWN_MODAL_CLASSES.forEach((cls) => document.body.classList.remove(cls))
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''

        // Remove only the backdrops React has abandoned. `.modal-backdrop` / `.offcanvas-backdrop`
        // are rendered by react-bootstrap, which deletes its own when the close animation ends —
        // taking one it still owns makes its removeChild() throw
        //   "NotFoundError: The node to be removed is not a child of this node"
        // which takes the whole SPA down. A backdrop React still owns belongs to a live component,
        // so leaving it alone is correct in every case, not just that one.
        document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop').forEach((element) => {
            if (!isReactOwned(element)) {
                element.remove()
            }
        })
    }, [location.pathname, location.search])
}

export default useRouteCleanup
